import { TableHead } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { kebabCase } from "lodash";
import { Column, OLTableActions, OrderColumn } from "./OLTable";
import { useStyles } from "./styles";

interface OLTableHeadProps {
  actions?: Array<OLTableActions>;
  classes: ReturnType<typeof useStyles>;
  columns: Column[];
  onSort?: (sortBy: OrderColumn) => void;
  selectable?: {
    disabled: boolean;
    allSelected: boolean;
    selectAll: () => void;
    unselectAll: () => void;
  };
  sort: OrderColumn;
  "data-test-id"?: string;
  disableActions?: boolean;
  columnRefs: React.MutableRefObject<(HTMLTableCellElement | null)[]>;
  calculateColumnLeftOffset: (index: number) => number;
}

export function OLTableHead({
  classes,
  columns,
  onSort,
  selectable,
  sort,
  "data-test-id": dataTestId,
  disableActions,
  columnRefs,
  calculateColumnLeftOffset,
}: OLTableHeadProps) {
  function changeSort(property: any) {
    if (!onSort) return;
    if (property !== sort.property)
      return onSort({
        property,
        order: "asc",
        rowsPerPage: sort.rowsPerPage,
        page: sort.page,
      });
    if (sort.order === "asc")
      return onSort({
        property,
        order: "desc",
        rowsPerPage: sort.rowsPerPage,
        page: sort.page,
      });
    else
      return onSort({
        property,
        order: "asc",
        rowsPerPage: sort.rowsPerPage,
        page: sort.page,
      });
  }

  return (
    <TableHead>
      <TableRow>
        {columns.map((column, index) => {
          const isFirstColumn = index === 0;
          const { isSticky } = column;
          const isLastColumn = columns.length === index + 1;

          return (
            <TableCell
              data-test-id={
                dataTestId &&
                `${dataTestId}-table-column-${kebabCase(column.label)}`
              }
              align={column.align || "inherit"}
              className={`${
                column.hasMinWidthThreshold ? classes.tableCellWithMinWidth : ""
              } ${classes.tableCell}`}
              key={column.label}
              sortDirection={
                sort.property === column.property ? sort.order : false
              }
              width={column.customWidth}
              ref={(el: HTMLTableCellElement | null) => {
                columnRefs.current[index] = el;
              }}
              sx={{
                ...(isSticky && {
                  position: "sticky !important",
                  left: calculateColumnLeftOffset(index),
                  zIndex: 3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  background: "inherit",
                }),
                ...(isLastColumn && {
                  borderRight: "none !important",
                }),
              }}
            >
              {selectable && isFirstColumn && (
                <Checkbox
                  checked={selectable.allSelected}
                  disabled={!selectable.allSelected && selectable.disabled}
                  className={classes.checkbox}
                  onClick={() => {
                    selectable.allSelected
                      ? selectable.unselectAll()
                      : selectable.selectAll();
                  }}
                />
              )}
              {column.isSortable ? (
                <>
                  {column.tooltip && (
                    <TooltipWithIcon
                      className={classes.TooltipInTable}
                      isWhite
                      placement="bottom-start"
                      text={column.tooltip}
                    />
                  )}
                  <TableSortLabel
                    data-test-id={
                      column.testId &&
                      column.testId + `-${column.property}-sort-property`
                    }
                    active={sort.property === column.property}
                    direction={
                      sort.property === column.property ? sort.order : "asc"
                    }
                    onClick={() => changeSort(column.property)}
                  >
                    {column.label}
                    {sort.property === column.property ? (
                      <span className={classes.visuallyHidden}>
                        {sort.order === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </>
              ) : (
                <>
                  {column.tooltip && (
                    <TooltipWithIcon
                      className={classes.TooltipInTable}
                      isWhite
                      placement="bottom-start"
                      text={column.tooltip}
                    />
                  )}
                  <span>{column.label}</span>
                </>
              )}
            </TableCell>
          );
        })}
        {!disableActions && (
          <TableCell
            className={classes.actionCell}
            style={{ padding: "0", position: "sticky" }}
          ></TableCell>
        )}
      </TableRow>
    </TableHead>
  );
}
